import { useEffect } from "react";

/**
 * Returns all iframes on the page, including those nested inside shadow roots.
 * `document.getElementsByTagName` does not pierce shadow DOM boundaries, so we
 * additionally query every element's shadowRoot to find hidden iframes.
 */
function getAllIframes(): HTMLIFrameElement[] {
  const iframes: HTMLIFrameElement[] = Array.from(document.getElementsByTagName("iframe"));
  document.querySelectorAll("*").forEach(el => {
    if (el.shadowRoot) {
      el.shadowRoot.querySelectorAll("iframe").forEach(iframe => {
        iframes.push(iframe as HTMLIFrameElement);
      });
    }
  });
  return iframes;
}

/**
 * Registers the H5P iframe resizer for the lifetime of the component.
 *
 * H5P content communicates with its parent page via `postMessage`. This hook
 * listens for those messages and resizes the iframe to fit the H5P content.
 *
 * The message listener is automatically removed when the component unmounts.
 */
export function useH5PResizer() {
  useEffect(() => {
    function receiveMessage(event: MessageEvent) {
      // Ignore messages not from H5P content.
      if (!event.data || event.data.context !== "h5p") return;

      // Match the message to the iframe that sent it.
      const iframe = getAllIframes().find(f => f.contentWindow === event.source);
      if (!iframe) return;

      // Helper to reply back to the H5P iframe.
      const respond = (action: string, data: Record<string, unknown> = {}) =>
        (event.source as Window).postMessage({ ...data, action, context: "h5p" }, event.origin);

      switch (event.data.action) {
        case "hello":
          // H5P is ready — make the iframe full-width and subscribe to window resizes.
          iframe.style.width = "100%";
          iframe.getBoundingClientRect(); // Chrome: force width update before content resizes
          window.addEventListener("resize", function resize() {
            if (iframe.contentWindow) respond("resize");
            else window.removeEventListener("resize", resize); // iframe was removed
          });
          respond("hello");
          break;

        case "prepareResize":
          // Reset iframe height to clientHeight so shrinking content is handled correctly,
          // then tell H5P it can proceed with the final resize.
          if (iframe.clientHeight !== event.data.scrollHeight || event.data.scrollHeight !== event.data.clientHeight) {
            iframe.style.height = `${event.data.clientHeight}px`;
            respond("resizePrepared");
          }
          break;

        case "resize":
          // Set the iframe height to the H5P content's scroll height.
          iframe.style.height = `${event.data.scrollHeight}px`;
          break;
      }
    }

    window.addEventListener("message", receiveMessage, false);
    return () => window.removeEventListener("message", receiveMessage);
  }, []);
}
